// frontend/shared-ui/src/forms/schemas/tool.js

// ─── Type classification ──────────────────────────────────────────────
// Every toolType maps to a "family". Families decide which groups show.
const STRIPPER_TYPES = [
    'Stripper',
    'Miller Stripper',
    'Mid-Span Buffer Tube Cutter',
];

const CUTTER_TYPES = [
    'Rotary Cutter',
    'Cable Cutter',
    'Sheath Slitter',
];

const CLEAVER_TYPES = ['Cleaver'];

const SPLICER_TYPES = ['Fusion Splicer'];

const CRIMP_TYPES = ['Crimping Tool'];

const KIT_TYPES = [
    'Tool Kit Box',
    'FTTH Tool Kit',
    'Preparation Kit',
    'Cleaning Kit',
];

const INSPECTION_TYPES = ['Inspection Scope'];

const CLEANING_TYPES = ['Cleaning Tool'];

const isOneOf = (v, list) => list.includes(v && v.toolType);
const isStripper = (v) => isOneOf(v, STRIPPER_TYPES);
const isCutter = (v) => isOneOf(v, CUTTER_TYPES);
const isCleaver = (v) => isOneOf(v, CLEAVER_TYPES);
const isSplicer = (v) => isOneOf(v, SPLICER_TYPES);
const isCrimp = (v) => isOneOf(v, CRIMP_TYPES);
const isKit = (v) => isOneOf(v, KIT_TYPES);
const isInspection = (v) => isOneOf(v, INSPECTION_TYPES);
const isCleaningTool = (v) => isOneOf(v, CLEANING_TYPES);

// ─── Static option sets ──────────────────────────────────────────────
const CLEAVER_TYPE_OPTIONS = [
    { value: 'FC-6S', label: 'FC-6S' },
    { value: 'FC-6M', label: 'FC-6M' },
    { value: 'Cold Cleaver', label: 'Cold Cleaver' },
    { value: 'Hot Cleaver', label: 'Hot Cleaver' },
    { value: 'None', label: 'None' },
];

const JACKET_MATERIAL_OPTIONS = [
    'PVC',
    'LSZH',
    'PE',
    'Nylon',
    'Kevlar',
    'Steel',
];

const TOOL_MATERIAL_OPTIONS = [
    'Metal',
    'Plastic',
    'Aluminum',
    'Stainless Material',
    'Rubber-grip',
    'Composite',
];

const STRIP_TYPE_OPTIONS = [
    { value: 'Outer Jacket', label: 'Outer Jacket' },
    { value: 'Buffer Tube', label: 'Buffer Tube' },
    { value: '250µm Coating', label: '250µm Coating' },
    { value: '900µm Coating', label: '900µm Coating' },
    { value: 'Multi-stage', label: 'Multi-stage' },
];

const CUT_TYPE_OPTIONS = [
    { value: 'Rotary', label: 'Rotary (score & snap)' },
    { value: 'Guillotine', label: 'Guillotine' },
    { value: 'Ratchet', label: 'Ratchet' },
    { value: 'Longitudinal', label: 'Longitudinal slitter' },
    { value: 'Circular', label: 'Circular cutter' },
];

const CLEAVE_ANGLE_OPTIONS = [
    { value: '0.5', label: '0.5° (precision)' },
    { value: '1.0', label: '1.0°' },
    { value: '1.5', label: '1.5°' },
    { value: '2.0', label: '2.0°' },
];

export default {
    slug: 'tool',
    label: 'Tools, Cutters & Tool Kits',
    defaultUnit: 'pcs',
    costDefaults: {
        avgVolumeCbm: 0.015,
        suggestedMarkupPct: 35,
        freightSensitivity: 'medium',
        note: 'Hand tools small; tool kits and full FTTH cases weigh more',
    },

    groups: [
        // ──────────────────────────────────────────────────────────────
        // 1. Basic — always shown
        // ──────────────────────────────────────────────────────────────
        {
            id: 'basic',
            label: 'Basic',
            columns: 2,
            fields: [
                {
                    key: 'toolType',
                    label: 'Tool Type',
                    type: 'select',
                    required: true,
                    options: [
                        // Cutting / stripping
                        'Cleaver',
                        'Fusion Splicer',
                        'Crimping Tool',
                        'Stripper',
                        'Miller Stripper',
                        'Mid-Span Buffer Tube Cutter',
                        'Rotary Cutter',
                        'Cable Cutter',
                        'Sheath Slitter',
                        // Cleaning / inspection
                        'Cleaning Tool',
                        'Cleaning Kit',
                        'Inspection Scope',
                        // Kits
                        'Tool Kit Box',
                        'FTTH Tool Kit',
                        'Preparation Kit',
                        // Other
                        'Other',
                    ],
                },
                {
                    key: 'material',
                    label: 'Body Material',
                    type: 'select',
                    options: TOOL_MATERIAL_OPTIONS,
                },
                {
                    key: 'compatibility',
                    label: 'Compatibility',
                    type: 'text',
                    placeholder: 'SC, LC, FC — or "universal"',
                },
                {
                    key: 'weight_g',
                    label: 'Weight',
                    type: 'number',
                    unit: 'g',
                },
            ],
        },

        // ──────────────────────────────────────────────────────────────
        // 2. STRIPPER-specific — only when toolType is a stripper
        // ──────────────────────────────────────────────────────────────
        {
            id: 'stripper',
            label: 'Stripper Specifications',
            columns: 2,
            when: isStripper,
            fields: [
                {
                    key: 'stripDiameterMin_mm',
                    label: 'Strip Diameter — Min',
                    type: 'number',
                    unit: 'µm',
                    step: 0.01,
                    hint: 'Smallest diameter this stripper can strip',
                },
                {
                    key: 'stripDiameterMax_mm',
                    label: 'Strip Diameter — Max',
                    type: 'number',
                    unit: 'µm',
                    step: 0.01,
                    hint: 'Largest diameter',
                },
                {
                    key: 'stripType',
                    label: 'Strip Stage',
                    type: 'select',
                    options: STRIP_TYPE_OPTIONS,
                },
                {
                    key: 'numberOfHoles',
                    label: 'Number of Strip Holes',
                    type: 'number',
                    hint: 'e.g. 3 holes for jacket / buffer / coating',
                },
                {
                    key: 'cassetteReplaceable',
                    label: 'Replaceable Cassette',
                    type: 'boolean',
                },
                {
                    key: 'stripsFiberOnly',
                    label: 'Fiber-only (no jacket)',
                    type: 'boolean',
                },
            ],
        },

        // ──────────────────────────────────────────────────────────────
        // 3. CUTTER / SLITTER-specific
        // ──────────────────────────────────────────────────────────────
        {
            id: 'cutter',
            label: 'Cutter Specifications',
            columns: 2,
            when: isCutter,
            fields: [
                {
                    key: 'cutType',
                    label: 'Cut Type',
                    type: 'select',
                    options: CUT_TYPE_OPTIONS,
                },
                {
                    key: 'cutDiameterMax_mm',
                    label: 'Max Cable Diameter',
                    type: 'number',
                    unit: 'mm',
                    step: 0.1,
                },
                {
                    key: 'bladeReplaceable',
                    label: 'Replaceable Blade',
                    type: 'boolean',
                },
                {
                    key: 'bladeLife',
                    label: 'Blade Life',
                    type: 'number',
                    unit: 'cuts',
                    hint: 'Number of cuts before replacement',
                },
                {
                    key: 'cutsJacket',
                    label: 'Cuts Outer Jacket',
                    type: 'boolean',
                },
                {
                    key: 'cutsBufferTube',
                    label: 'Cuts Buffer Tube',
                    type: 'boolean',
                },
            ],
        },

        // ──────────────────────────────────────────────────────────────
        // 4. CLEAVER-specific
        // ──────────────────────────────────────────────────────────────
        {
            id: 'cleaver',
            label: 'Cleaver Specifications',
            columns: 2,
            when: isCleaver,
            fields: [
                {
                    key: 'cleaverType',
                    label: 'Cleaver Model',
                    type: 'select',
                    options: CLEAVER_TYPE_OPTIONS,
                },
                {
                    key: 'cleaveAngle',
                    label: 'Typical Cleave Angle',
                    type: 'select',
                    options: CLEAVE_ANGLE_OPTIONS,
                },
                {
                    key: 'bladeLife',
                    label: 'Blade Life',
                    type: 'number',
                    unit: 'splices',
                    hint: 'Rated splices per blade position',
                },
                {
                    key: 'bladePositions',
                    label: 'Blade Positions',
                    type: 'number',
                    hint: 'e.g. 16 for a 16-position rotating blade',
                },
                {
                    key: 'coatingRangeMin_um',
                    label: 'Coating — Min',
                    type: 'number',
                    unit: 'µm',
                },
                {
                    key: 'coatingRangeMax_um',
                    label: 'Coating — Max',
                    type: 'number',
                    unit: 'µm',
                },
            ],
        },

        // ──────────────────────────────────────────────────────────────
        // 5. FUSION SPLICER-specific
        // ──────────────────────────────────────────────────────────────
        {
            id: 'splicer',
            label: 'Fusion Splicer Specifications',
            columns: 2,
            when: isSplicer,
            fields: [
                {
                    key: 'spliceLoss',
                    label: 'Typical Splice Loss',
                    type: 'number',
                    unit: 'dB',
                    step: 0.01,
                },
                {
                    key: 'spliceTime',
                    label: 'Typical Splice Time',
                    type: 'number',
                    unit: 's',
                    step: 0.1,
                },
                {
                    key: 'arcPower',
                    label: 'Arc Power Range',
                    type: 'text',
                    placeholder: 'e.g. 10–60',
                },
                {
                    key: 'fibersSupported',
                    label: 'Fibers Supported',
                    type: 'text',
                    placeholder: 'SM, MM, DS, NZDS',
                },
                {
                    key: 'batteryLife',
                    label: 'Battery Life',
                    type: 'number',
                    unit: 'splices',
                },
                {
                    key: 'hasTouchscreen',
                    label: 'Touchscreen',
                    type: 'boolean',
                },
            ],
        },

        // ──────────────────────────────────────────────────────────────
        // 6. CRIMPING TOOL-specific
        // ──────────────────────────────────────────────────────────────
        {
            id: 'crimp',
            label: 'Crimping Tool Specifications',
            columns: 2,
            when: isCrimp,
            fields: [
                {
                    key: 'crimpProfile',
                    label: 'Crimp Profile',
                    type: 'text',
                    placeholder: 'e.g. hex, square, RJ45',
                },
                {
                    key: 'crimpRangeMin_mm',
                    label: 'Crimp Range — Min',
                    type: 'number',
                    unit: 'mm',
                    step: 0.01,
                },
                {
                    key: 'crimpRangeMax_mm',
                    label: 'Crimp Range — Max',
                    type: 'number',
                    unit: 'mm',
                    step: 0.01,
                },
                {
                    key: 'ratchetMechanism',
                    label: 'Ratchet Mechanism',
                    type: 'boolean',
                },
            ],
        },

        // ──────────────────────────────────────────────────────────────
        // 7. KIT-specific — parts count, cleaver, VFL, power meter
        // ──────────────────────────────────────────────────────────────
        {
            id: 'kit',
            label: 'Kit Contents',
            columns: 2,
            when: isKit,
            fields: [
                {
                    key: 'numberOfParts',
                    label: 'Number of Parts',
                    type: 'number',
                    min: 1,
                    hint: 'Total pieces in the case',
                },
                {
                    key: 'cleaverType',
                    label: 'Included Cleaver',
                    type: 'select',
                    options: CLEAVER_TYPE_OPTIONS,
                },
                {
                    key: 'powerMeter',
                    label: 'Includes Power Meter',
                    type: 'boolean',
                },
                {
                    key: 'vflRange_km',
                    label: 'VFL Range',
                    type: 'number',
                    unit: 'km',
                },
                {
                    key: 'hasFusionSplicer',
                    label: 'Includes Fusion Splicer',
                    type: 'boolean',
                },
                {
                    key: 'hasStripper',
                    label: 'Includes Stripper',
                    type: 'boolean',
                },
                {
                    key: 'hasCutter',
                    label: 'Includes Cutter',
                    type: 'boolean',
                },
                {
                    key: 'caseType',
                    label: 'Case Type',
                    type: 'select',
                    options: ['Soft Bag', 'Hard Case', 'Toolbox', 'None'],
                },
                {
                    key: 'kitContents',
                    label: 'Kit Contents (detail)',
                    type: 'text',
                    hint: 'Free-form list if above checkboxes are not enough',
                },
            ],
        },

        // ──────────────────────────────────────────────────────────────
        // 8. INSPECTION SCOPE-specific
        // ──────────────────────────────────────────────────────────────
        {
            id: 'inspection',
            label: 'Inspection Scope Specifications',
            columns: 2,
            when: isInspection,
            fields: [
                {
                    key: 'magnification',
                    label: 'Magnification',
                    type: 'text',
                    placeholder: 'e.g. 200× / 400×',
                },
                {
                    key: 'connectorSupport',
                    label: 'Connector Adapters',
                    type: 'text',
                    placeholder: 'SC, LC, FC, MU',
                },
                {
                    key: 'hasDisplay',
                    label: 'Has Display Screen',
                    type: 'boolean',
                },
            ],
        },

        // ──────────────────────────────────────────────────────────────
        // 9. CLEANING TOOL-specific
        // ──────────────────────────────────────────────────────────────
        {
            id: 'cleaning',
            label: 'Cleaning Tool Specifications',
            columns: 2,
            when: isCleaningTool,
            fields: [
                {
                    key: 'cleanerType',
                    label: 'Cleaner Type',
                    type: 'select',
                    options: ['Pen', 'Cartridge', 'Cassette', 'Swab', 'Wipe'],
                },
                {
                    key: 'cleanerUses',
                    label: 'Rated Uses',
                    type: 'number',
                    hint: 'Number of cleanings before replacement',
                },
                {
                    key: 'cleansConnectorEnd',
                    label: 'Cleans Connector End-face',
                    type: 'boolean',
                },
                {
                    key: 'cleansBulkhead',
                    label: 'Cleans Bulkhead Adapter',
                    type: 'boolean',
                },
            ],
        },
    ],

    // ─── SKU generation ──────────────────────────────────────────────────
    buildSku(v) {
        const codes = {
            'Cleaver': 'CLV',
            'Fusion Splicer': 'SPL',
            'Crimping Tool': 'CRMP',
            'Stripper': 'STRP',
            'Miller Stripper': 'MSTRP',
            'Mid-Span Buffer Tube Cutter': 'MBTC',
            'Rotary Cutter': 'RCUT',
            'Cable Cutter': 'CCUT',
            'Sheath Slitter': 'SLIT',
            'Cleaning Tool': 'CLN',
            'Cleaning Kit': 'CLNKIT',
            'Inspection Scope': 'INS',
            'Tool Kit Box': 'KIT',
            'FTTH Tool Kit': 'FTTHKIT',
            'Preparation Kit': 'PREP',
            'Other': 'OTH',
        };
        const code = codes[v.toolType] || 'TOOL';

        const parts = ['TOOL', code];

        // Strippers — include strip range so SKU differentiates models
        if (isStripper(v)) {
            const rng =
                v.stripDiameterMin_mm != null && v.stripDiameterMax_mm != null
                    ? `${v.stripDiameterMin_mm}-${v.stripDiameterMax_mm}MM`
                    : '';
            if (rng) parts.push(rng);
            if (v.numberOfHoles) parts.push(`${v.numberOfHoles}H`);
        }

        // Cutters — max diameter
        if (isCutter(v) && v.cutDiameterMax_mm) {
            parts.push(`MAX${v.cutDiameterMax_mm}MM`);
        }

        // Cleavers / splicers
        if (isCleaver(v) && v.cleaverType && v.cleaverType !== 'None') {
            parts.push(v.cleaverType.replace(/\s+/g, '').toUpperCase());
        }
        if (isSplicer(v) && v.fibersSupported) {
            parts.push(String(v.fibersSupported).replace(/[^A-Z0-9]/gi, '').toUpperCase());
        }

        // Kits — parts count and inclusions
        if (isKit(v)) {
            if (v.numberOfParts) parts.push(`${v.numberOfParts}PC`);
            if (v.cleaverType && v.cleaverType !== 'None') {
                parts.push(v.cleaverType.replace(/\s+/g, '').toUpperCase());
            }
            if (v.powerMeter) parts.push('PM');
            if (v.vflRange_km) parts.push(`VFL${v.vflRange_km}KM`);
        }

        return parts
            .filter(Boolean)
            .join('-')
            .toUpperCase()
            .replace(/[^A-Z0-9\-]/g, '');
    },

    // ─── Name generation ─────────────────────────────────────────────────
    buildName(v) {
        const bits = [];

        if (!v.toolType) return 'Tool';

        // Stripper: "3-Hole Fiber Stripper (0.125–0.25mm, 250µm Coating)"
        if (isStripper(v)) {
            if (v.numberOfHoles) bits.push(`${v.numberOfHoles}-Hole`);
            bits.push(v.toolType);
            const rng =
                v.stripDiameterMin_mm != null && v.stripDiameterMax_mm != null
                    ? `(${v.stripDiameterMin_mm}–${v.stripDiameterMax_mm}µm)`
                    : '';
            if (rng) bits.push(rng);
            if (v.stripType) bits.push(`— ${v.stripType}`);
            return bits.filter(Boolean).join(' ').trim();
        }

        // Cutter: "Rotary Cutter — max 25mm"
        if (isCutter(v)) {
            bits.push(v.toolType);
            if (v.cutDiameterMax_mm) bits.push(`— max ${v.cutDiameterMax_mm}`);
            if (v.cutType) bits.push(`(${v.cutType})`);
            return bits.filter(Boolean).join(' ').trim();
        }

        // Cleaver: "FC-6S Cleaver (1.0°, 16 positions)"
        if (isCleaver(v)) {
            if (v.cleaverType && v.cleaverType !== 'None') bits.push(v.cleaverType);
            bits.push('Cleaver');
            if (v.cleaveAngle) bits.push(`(${v.cleaveAngle}°)`);
            if (v.bladePositions) bits.push(`— ${v.bladePositions} positions`);
            return bits.filter(Boolean).join(' ').trim();
        }

        // Splicer
        if (isSplicer(v)) {
            bits.push(v.toolType);
            if (v.fibersSupported) bits.push(`(${v.fibersSupported})`);
            return bits.filter(Boolean).join(' ').trim();
        }

        // Kit: "FTTH Tool Kit — 12 pcs, FC-6S Cleaver + Power Meter + 5km VFL"
        if (isKit(v)) {
            bits.push(v.toolType);
            if (v.numberOfParts) bits.push(`— ${v.numberOfParts} pcs`);
            const extras = [];
            if (v.cleaverType && v.cleaverType !== 'None') {
                extras.push(`${v.cleaverType} Cleaver`);
            }
            if (v.powerMeter) extras.push('Power Meter');
            if (v.vflRange_km) extras.push(`${v.vflRange_km}km VFL`);
            if (v.hasFusionSplicer) extras.push('Fusion Splicer');
            if (v.hasStripper) extras.push('Stripper');
            if (v.hasCutter) extras.push('Cutter');
            if (extras.length) bits.push(`+ ${extras.join(' + ')}`);
            return bits.filter(Boolean).join(' ').trim();
        }

        // Fallback
        if (v.compatibility) bits.push(`(${v.compatibility})`);
        bits.push(v.toolType);
        return bits.filter(Boolean).join(' ').trim();
    },

    // ─── Detail view ─────────────────────────────────────────────────────
    attributeSchema: [
        { key: 'toolType', label: 'Tool Type' },
        { key: 'material', label: 'Body Material' },
        { key: 'compatibility', label: 'Compatibility' },
        { key: 'weight_g', label: 'Weight', unit: 'g' },

        // Stripper
        { key: 'stripDiameterMin_mm', label: 'Strip Diameter Min', unit: 'mm' },
        { key: 'stripDiameterMax_mm', label: 'Strip Diameter Max', unit: 'mm' },
        { key: 'stripType', label: 'Strip Stage' },
        { key: 'numberOfHoles', label: 'Strip Holes' },
        { key: 'cassetteReplaceable', label: 'Replaceable Cassette' },
        { key: 'stripsFiberOnly', label: 'Fiber-only' },

        // Cutter
        { key: 'cutType', label: 'Cut Type' },
        { key: 'cutDiameterMax_mm', label: 'Max Cable Diameter', unit: 'mm' },
        { key: 'bladeReplaceable', label: 'Replaceable Blade' },
        { key: 'bladeLife', label: 'Blade Life' },
        { key: 'cutsJacket', label: 'Cuts Outer Jacket' },
        { key: 'cutsBufferTube', label: 'Cuts Buffer Tube' },

        // Cleaver
        { key: 'cleaverType', label: 'Cleaver Model' },
        { key: 'cleaveAngle', label: 'Cleave Angle', unit: '°' },
        { key: 'bladePositions', label: 'Blade Positions' },
        { key: 'coatingRangeMin_um', label: 'Coating Min', unit: 'µm' },
        { key: 'coatingRangeMax_um', label: 'Coating Max', unit: 'µm' },

        // Splicer
        { key: 'spliceLoss', label: 'Splice Loss', unit: 'dB' },
        { key: 'spliceTime', label: 'Splice Time', unit: 's' },
        { key: 'arcPower', label: 'Arc Power Range' },
        { key: 'fibersSupported', label: 'Fibers Supported' },
        { key: 'batteryLife', label: 'Battery Life', unit: 'splices' },
        { key: 'hasTouchscreen', label: 'Touchscreen' },

        // Crimp
        { key: 'crimpProfile', label: 'Crimp Profile' },
        { key: 'crimpRangeMin_mm', label: 'Crimp Range Min', unit: 'mm' },
        { key: 'crimpRangeMax_mm', label: 'Crimp Range Max', unit: 'mm' },
        { key: 'ratchetMechanism', label: 'Ratchet Mechanism' },

        // Kit
        { key: 'numberOfParts', label: 'Number of Parts' },
        { key: 'powerMeter', label: 'Power Meter' },
        { key: 'vflRange_km', label: 'VFL Range', unit: 'km' },
        { key: 'hasFusionSplicer', label: 'Fusion Splicer Included' },
        { key: 'hasStripper', label: 'Stripper Included' },
        { key: 'hasCutter', label: 'Cutter Included' },
        { key: 'caseType', label: 'Case Type' },
        { key: 'kitContents', label: 'Kit Contents (detail)' },

        // Inspection
        { key: 'magnification', label: 'Magnification' },
        { key: 'connectorSupport', label: 'Connector Adapters' },
        { key: 'hasDisplay', label: 'Has Display' },

        // Cleaning
        { key: 'cleanerType', label: 'Cleaner Type' },
        { key: 'cleanerUses', label: 'Rated Uses' },
        { key: 'cleansConnectorEnd', label: 'Cleans Connector End-face' },
        { key: 'cleansBulkhead', label: 'Cleans Bulkhead Adapter' },
    ],
};